import ApiService from '@/services/apiService';
import { mapState } from 'vuex';
import routerPaths from '@/router/paths';
import { themeDefaults, themeColors, vuetifyColors } from '@/utils/themes';
import customColorMixin from '@/mixins/customColor';
import { get, isEqual, isEmpty } from 'lodash';
import { EventBus } from '@/services/eventbus.js';
export default {
  name: 'App',
  mixins: [customColorMixin],
  data() {
    return {
      eulaKey: 'initial',
      brandingLoaded: false,
      breadcrumbs: null,
      pageTitle: {},
      themesData: {},
      footer: {},
      user: {},
      // Guard to ensure only the latest branding request applies
      brandingRequestId: 0
    };
  },
  computed: {
    ...mapState('app', ['load', 'loading', 'language', 'UiTheme', 'color']),
    userId() {
      return get(this, '$store.state.authentication.user.id') || null;
    },
    eulaAccepted() {
      return get(this, '$store.state.authentication.user.properties.eula.accepted', false) === true;
    },
    authContextKey() {
      return JSON.stringify({
        status: get(this, '$store.state.authentication.status', ''),
        userId: this.userId,
        tenantId: get(this, '$store.state.authentication.tenantId', null),
        eulaAccepted: this.eulaAccepted,
        twoFactorSetupState: get(this, '$store.state.authentication.twoFactorSetup.state', ''),
        twoFactorSetupRequired: get(this, '$store.state.authentication.twoFactorSetup.setupRequired', false),
        twoFactorEnabled: get(this, '$store.state.authentication.user.properties.2fa.enabled', false)
      });
    },
    showEulaDialog() {
      return !!this.userId && get(this, '$store.state.authentication.isImpersonating', false) === false && get(this, '$store.state.authentication.status', '') === 'success' && get(this, '$store.state.authentication.user.properties.eula.accepted', false) === false && get(this.$route, 'meta.noAuth', false) === false;
    },
    fontClass() {
      return this.$store.state.app.useAltFonts ? 'font-alt' : '';
    },
    showAppLayout() {
      if (get(this.$route, 'meta.hideLayout')) {
        return false;
      }
      switch (this.$route.path) {
        case '/login':
        case '/error':
        case '/reset-password':
        case '/change-password':
        case '/oauth':
        case '/':
          return false;
        default:
          return true;
      }
    },
    fireflyHost() {
      return process.env.VUE_APP_FIREFLY_HOST;
    },
    host() {
      // Only hostname is required for theming domain matching (no protocol/port)
      return window.location.hostname;
    }
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        this.updateBreadcrumbs();
      });
    },
    language() {
      this.$nextTick(() => {
        this.updateBreadcrumbs();
      });
    },
    authContextKey: {
      immediate: true,
      async handler() {
        await this.syncAuthenticatedUserContext();
      }
    }
  },
  methods: {
    // eslint-disable-next-line max-statements
    async syncAuthenticatedUserContext() {
      if (!this.userId) {
        this.user = {};
        await this.loadBranding();
        return;
      }
      try {
        const user = await ApiService.getUser(this);
        this.user = user;
        await this.loadBranding();
        const defaults = get(user, 'properties.userDefaults');
        if (defaults && !isEmpty(defaults)) {
          this.setLang(user);
          this.setColor(user);
          this.$store.commit('app/setMapStyle', user.properties.userDefaults.mapLayout);
          this.setUiTheme(user);
          this.$store.commit('app/setDrawer', user.properties.userDefaults.drawer);
          this.$store.commit('app/setMiniVariant', user.properties.userDefaults.miniVariant);
          this.$store.commit('app/setMap3DBuild', user.properties.userDefaults.map3DBuild);
          this.$store.commit('app/setRowsPerPage', user.properties.userDefaults.pagination);
        }
      } catch (e) {
        console.error('error getting user ', e);
      }
      const tenantID = get(this, '$store.state.authentication.tenantId');
      if (tenantID) {
        // ensure permissions are available before deciding whether to fetch tenant data
        await this.ensurePermissionsLoaded();
        // Perform a scope-specific permission check against the primary scope to mirror backend behavior
        const primaryScopeId = get(this, '$store.state.authentication.primaryScopeIds[0]');
        try {
          if (primaryScopeId) {
            const res = await ApiService.checkAccesses([{
              subject: 'DigitalTwin',
              action: 'read'
            }, {
              subject: 'DigitalTwin',
              action: 'manage'
            }], {
              scopeId: primaryScopeId
            });
            const canReadTenant = res['DigitalTwin.read'] || res['DigitalTwin.manage'];
            if (canReadTenant) {
              this.getTenantData(tenantID);
            } else {
              console.info('Missing tenant read permissions on primary scope; skipping tenant data loading');
            }
          } else {
            console.info('No primary scope found; skipping tenant data loading');
          }
        } catch (e) {
          console.info('Permission check failed; skipping tenant data loading');
        }
      }
    },
    async ensurePermissionsLoaded() {
      if (!get(this, '$store.state.app.permissions')) {
        try {
          await this.$store.dispatch('app/loadPermissions');
        } catch (e) {
          // ignore; permissions loading failure shouldn't block app
        }
      }
    },
    setLang(user) {
      this.$store.commit('app/language', {
        payload: user.properties.userDefaults.language,
        context: this
      });
    },
    setColor(user) {
      const mode = this.$store.state.app.UiTheme ? 'dark' : 'light';
      const colorKey = user.properties.userDefaults.color;
      if (colorKey === 'customerColor') {
        const resolvedCustomColor = user.properties.userDefaults.customColor;
        this.$store.commit('app/setColor', mode === 'dark' ? 'customerColorDark' : 'customerColorLight');
        this.$store.commit('app/setColorHexLight', resolvedCustomColor);
        this.$store.commit('app/setColorHexDark', resolvedCustomColor);
        this.$vuetify.theme.themes.light.primary = {
          base: resolvedCustomColor
        };
        this.$vuetify.theme.themes.dark.primary = {
          base: resolvedCustomColor
        };
      } else {
        this.$store.commit('app/setColor', user.properties.userDefaults.color);
        const baseColor = vuetifyColors[user.properties.userDefaults.color] || user.properties.userDefaults.color;
        this.$vuetify.theme.themes.light.primary = {
          base: baseColor
        };
        this.$vuetify.theme.themes.dark.primary = {
          base: baseColor
        };
      }
      this.$store.commit('app/setThingshiveColorLight', this.$store.state.app.color);
      this.$store.commit('app/setThingshiveColorDark', this.$store.state.app.color);
    },
    setUiTheme(user) {
      const mode = user.properties.userDefaults.uiTheme ? 'dark' : 'light';
      this.$store.commit('app/setThingshiveTheme', mode);
      this.$store.commit('app/setMode', mode);
      this.$store.commit('app/setUiTheme', mode === 'dark');
      this.$vuetify.theme.dark = user.properties.userDefaults.uiTheme;
      const customColor = user.properties.userDefaults.customColor;
      const colorKey = user.properties.userDefaults.color;
      if (colorKey === 'customerColor' && this.isHexColor(customColor)) {
        this.$vuetify.theme.themes.light.primary = {
          base: customColor
        };
        this.$vuetify.theme.themes.dark.primary = {
          base: customColor
        };
      }
    },
    isHexColor(color) {
      return /^#([0-9A-F]{3}){1,2}$/i.test(color);
    },
    get,
    updateBreadcrumbs() {
      let newBreadCrumbsPath = null;
      const routerView = get(this.$refs, 'view.$refs.routerview');
      if (routerView && routerView.breadCrumbsPath) {
        newBreadCrumbsPath = routerView.breadCrumbsPath();
      }
      if (!isEqual(this.breadcrumbs, newBreadCrumbsPath)) {
        this.breadcrumbs = newBreadCrumbsPath;
      }
    },
    getLocalUserColor(storageKey, fallback = 'primary') {
      const localColor = this.$store.state.app[storageKey];
      if (themeColors.indexOf(localColor) >= 0) {
        return localColor;
      }
      return fallback;
    },
    getSideBarIconLink(sidebarIconLink) {
      const validLink = routerPaths.filter(path => (path.meta || {}).isDrawerIconLink && path.path === sidebarIconLink);
      return (validLink[0] || {}).path || '/home';
    },
    setFavicon(theme) {
      // Remove existing favicon tags to avoid race/order issues
      const existingIcons = Array.from(document.querySelectorAll('link[rel*="icon"]'));
      existingIcons.forEach(el => el.parentNode && el.parentNode.removeChild(el));
      const link = document.createElement('link');
      link.type = 'image/png';
      link.rel = 'icon';
      if (!theme.favicon || theme.favicon === themeDefaults.favicon) {
        link.href = 'favicon/niotix-x-144x144.png';
      } else {
        link.href = location.origin + theme.favicon;
      }
      document.head.appendChild(link);
    },
    // eslint-disable-next-line max-statements
    async loadBranding() {
      // Create a new request id and capture it locally
      const currentReqId = Date.now();
      this.brandingRequestId = currentReqId;
      const tenantId = get(this.$store, 'state.authentication.tenantIdThemeSetting.id') || get(this.$store, 'state.authentication.primaryTenantIds[0]') || get(this.$store, 'state.authentication.tenantId');
      // Only apply a persisted user theme override when a user is authenticated.
      // On login/logout transitions we want the domain/tenant branding mode, not stale user mode.
      const hasAuthenticatedUser = !!get(this.$store, 'state.authentication.user.id');
      let userMode = null;
      if (hasAuthenticatedUser) {
        userMode = String(this.$store.state.app.thingshiveTheme).toLocaleLowerCase();
        if (userMode !== 'dark' && userMode !== 'light') {
          userMode = this.$store.state.app.mode || null;
        }
      }

      // Clear any previously remembered per-user color overrides so branding highlights take effect
      this.$store.commit('app/setThingshiveColorLight', undefined);
      this.$store.commit('app/setThingshiveColorDark', undefined);
      const theme = await this.$store.dispatch('app/loadBranding', {
        hostUri: this.host,
        tenantId,
        userThemeMode: userMode,
        context: this
      });
      // Ignore stale responses
      if (this.brandingRequestId !== currentReqId) {
        return;
      }
      this.pageTitle = this.$store.state.app.pageTitle;
      this.footer = this.$store.state.app.footer;
      this.$store.state.app.sidebarIconLink = this.getSideBarIconLink(theme.sidebarIconLink);
      this.$store.state.app.grafanaUrl = theme.grafanaUrl;
      this.$store.state.app.fireflyUrl = `https://${this.fireflyHost}`;

      // use vollkorn as default font as long as the digimondo logo was not changed
      const useAltFonts = theme.useAltFonts === undefined ? !this.isDefaultSvgLogo() : theme.useAltFonts;
      this.$store.commit('app/useAltFonts', {
        payload: useAltFonts
      });
      this.$store.state.app.useCustomBackground = theme.useCustomBackground === undefined ? themeDefaults.useCustomBackground : theme.useCustomBackground;

      // Ignore if a newer request has started since computing URLs
      if (this.brandingRequestId !== currentReqId) {
        return;
      }
      this.setFavicon(theme);

      // set vue ui mode
      this.$vuetify.theme.dark = this.themeGetMode() === 'dark';
      this.themesData = this.$store.state.app.themes;

      // Apply branding highlight colors into runtime store (without persisting to user)
      const lightHex = get(theme, 'lightHighlightCustomer') || null;
      const darkHex = get(theme, 'darkHighlightCustomer') || null;
      this.$store.commit('app/setColorHexLight', lightHex);
      this.$store.commit('app/setColorHexDark', darkHex);
      this.$store.commit('app/setThingshiveColorLight', lightHex || get(theme, 'lightHighlight'));
      this.$store.commit('app/setThingshiveColorDark', darkHex || get(theme, 'darkHighlight'));
      const currentMode = this.themeGetMode();
      const resolvedKey = mode => {
        if (mode === 'dark') {
          return darkHex ? 'customerColorDark' : get(theme, 'darkHighlight');
        }
        return lightHex ? 'customerColorLight' : get(theme, 'lightHighlight');
      };
      this.$store.commit('app/setColor', resolvedKey(currentMode));

      // Apply Vuetify primary colors from current branding highlights
      try {
        const themes = this.$store.state.app.themes || {};
        const light = themes.light || {};
        const dark = themes.dark || {};
        const lightBase = this.$store.state.app.colorHexLight || light.highlightCustomer || vuetifyColors[light.highlight] || light.highlight || vuetifyColors.primary;
        const darkBase = this.$store.state.app.colorHexDark || dark.highlightCustomer || vuetifyColors[dark.highlight] || dark.highlight || vuetifyColors.primary;
        this.$vuetify.theme.themes.light.primary = {
          base: lightBase
        };
        this.$vuetify.theme.themes.dark.primary = {
          base: darkBase
        };
      } catch (e) {
        // no-op safeguard
      }
      this.brandingLoaded = true;
      // Signal that branding has been fully applied so dependent components can refresh assets
      EventBus.$emit('branding-applied');
    },
    getTenantData(tenantId) {
      ApiService.tenantGetById(tenantId, ['no-descendents']).then(tenants => {
        const feedbackEmail = get(tenants, 'data.feedback.bug') || 'support@digimondo.de';
        this.$store.commit('app/setFeedbackEmail', feedbackEmail);
      }).catch(error => {
        // only print error-messages for tenant-data, if this is not a redirect
        // in case of Node-RED or Grafana opening, it will shortly display the login-page before redirecting;
        // anything other than the Auth shall not disturb the user, so an error fetching the tenant-data is not pertinent
        if (!get(this.$router, 'query.redirect')) {
          const msg = get(error, 'response.data.message');
          const status = get(error, 'response.status');
          const isExpectedPermissionError = status === 401 && typeof msg === 'string' && msg.startsWith('NO_PERMISSION_');
          if (!isExpectedPermissionError) {
            if (error.response && this.$i18n_te(`Error.${msg}`)) {
              this.$snack(this.$i18n_t(`Error.${msg}`), 'error');
            } else {
              this.$snack(this.$i18n_t('Common.tenantReadFail'), 'error');
            }
          }
        }
        this.$store.commit('app/setFeedbackEmail', 'support@digimondo.de');
      }).finally(() => {
        this.$store.state.app.loading = false;
      });
    }
  }
};